/**
 * Cálculo de IVA (MX) sobre precios tax-inclusive.
 *
 * Convención NQORO (ADR-002): el precio almacenado ES el precio final visible
 * al cliente (IVA 16% incluido). Este módulo deriva el subtotal/IVA para
 * reportes, facturación y order summary.
 *
 * Todos los montos están en CENTAVOS para evitar floating point error.
 */

export const IVA_RATE_MX = 0.16;

export interface IvaBreakdown {
  /** Precio total (lo que ve y paga el cliente). */
  totalCents: number;
  /** Subtotal sin IVA. */
  subtotalCents: number;
  /** Cantidad de IVA incluido. */
  ivaCents: number;
}

/** Dado un precio tax-inclusive en centavos, devuelve subtotal + iva. */
export function deriveIvaBreakdown(totalCents: number): IvaBreakdown {
  if (!Number.isFinite(totalCents) || totalCents < 0) {
    throw new RangeError(`totalCents inválido: ${totalCents}`);
  }
  /**
   * Cálculo:  total = subtotal * (1 + IVA_RATE)
   *           subtotal = total / 1.16
   * Redondeamos subtotal a entero (bankers rounding por ser tabla de precios MX).
   * iva = total - subtotal (garantiza que la suma vuelve a dar el total exacto).
   */
  const subtotalCents = Math.round(totalCents / (1 + IVA_RATE_MX));
  const ivaCents = totalCents - subtotalCents;
  return { totalCents, subtotalCents, ivaCents };
}

/** Suma montos tax-inclusive y retorna el breakdown acumulado. */
export function aggregateIvaBreakdown(...totals: number[]): IvaBreakdown {
  const sum = totals.reduce((acc, n) => acc + n, 0);
  return deriveIvaBreakdown(sum);
}

/** Convierte un precio tax-exclusive a tax-inclusive (uso raro — solo migraciones). */
export function applyIvaToExclusive(subtotalCents: number): number {
  if (subtotalCents < 0) throw new RangeError('subtotalCents negativo');
  return Math.round(subtotalCents * (1 + IVA_RATE_MX));
}

/** Formato humano MXN con IVA incluido (para UI). */
export function formatIvaMessage(totalCents: number): string {
  const { ivaCents } = deriveIvaBreakdown(totalCents);
  const fmt = new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    maximumFractionDigits: 0,
  });
  return `Incluye IVA 16% · ${fmt.format(ivaCents / 100)}`;
}
